/**
 * <collection-pagination> — client-side paging for collection grids.
 *
 * Lives inside [data-products-grid] on purpose: the filter form replaces that
 * container's innerHTML, and custom elements upgrade on insertion while <script>
 * tags do not. That is what keeps paging alive after a filter change.
 */
if (!customElements.get('collection-pagination')) {
  class CollectionPagination extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('[data-products-grid]') || this.parentElement;
      if (!this.container) return;

      this.sectionId = this.getAttribute('data-section-id') || '';
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 24;
      this.mode = this.getAttribute('data-pagination-type') || '';
      this.partition(this.gridItems());

      if (this.mode === 'load_more' || this.mode === 'load_more_auto') {
        this.initLoadMore();
      } else {
        this.initPaged();
      }
    }

    gridItems() {
      return Array.from(
        this.container.querySelectorAll('.grid-item:not(.product-item--featured-image)')
      );
    }

    /**
     * A `paginate` window holds both streams, in-stock then sold-out, because the section
     * renders in two passes. Sorting is therefore only correct inside one window. Tracking
     * the two streams separately lets appended windows merge into the right stream instead
     * of landing after the previous window's sold-out block.
     */
    partition(items) {
      this.inStock = [];
      this.soldOut = [];
      items.forEach((item) => {
        (item.classList.contains('product-item--sold-out') ? this.soldOut : this.inStock).push(item);
      });
      this.rebuildOrder();
    }

    rebuildOrder() {
      this.products = this.inStock.concat(this.soldOut);
      this.totalPages = Math.max(1, Math.ceil(this.products.length / this.perPage));
    }

    scrollToTop() {
      const section = this.closest('[data-section-id]') || this.container;
      if (!section) return;
      window.scrollTo({ top: section.offsetTop - 100, behavior: 'smooth' });
    }

    // ── Load more ───────────────────────────────────────────────────────────
    initLoadMore() {
      this.loadMoreContainer = this.container.querySelector('[data-load-more-container]');
      this.loadMoreBtn = this.container.querySelector('[data-load-more-btn]');
      this.itemsParent =
        this.container.querySelector('.grid') ||
        (this.products[0] && this.products[0].parentElement);
      // Read before the first reveal: revealLimit() depends on it, and a mostly-sold-out first
      // window would otherwise show sold-out cards in the opening screenful.
      this.nextUrl = this.loadMoreContainer
        ? this.loadMoreContainer.getAttribute('data-next-url') || ''
        : '';
      this.isFetching = false;
      this.isPrefetching = false;
      this.pendingReveal = false;
      this.prefetchFailed = false;
      // Distinct from "nextUrl is empty": only the server saying there is no further window
      // means the collection is complete. A failed fetch must not masquerade as the end.
      this.exhausted = !this.nextUrl;

      this.visibleCount = Math.min(this.perPage, this.revealLimit());
      this.products.forEach((product, index) => {
        product.style.display = index < this.visibleCount ? '' : 'none';
      });

      this.updateLoadMoreVisibility();

      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () => this.loadMore());
      }

      if (this.mode === 'load_more_auto') {
        const sentinel = this.container.querySelector('[data-load-more-sentinel]');
        if (sentinel && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && this.hasMore()) {
                  this.loadMore(true);
                }
              });
            },
            { rootMargin: '300px' }
          );
          this.observer.observe(sentinel);
        }
      }

      // Only fires when the first window holds fewer in-stock items than one reveal, which
      // puts visibleCount at the limit before any click. Normal collections open far below it.
      this.maybePrefetch();
    }

    hasMore() {
      return this.visibleCount < this.products.length || !!this.nextUrl;
    }

    /**
     * The visual-filters section writes style.display on every grid item whenever its client-side
     * search runs, and a MutationObserver re-runs that on any grid change — including our own
     * appends. Both owners cannot win, so the filter wins while it is active (it must be free to
     * show matches from any window) and calls this to hand the slice back when it clears.
     */
    reapplyVisibility() {
      if (this.mode !== 'load_more' && this.mode !== 'load_more_auto') return;
      this.products.forEach((product, index) => {
        product.style.display = index < this.visibleCount ? '' : 'none';
      });
      this.updateLoadMoreVisibility();
    }

    updateLoadMoreVisibility() {
      if (!this.loadMoreContainer) return;
      this.loadMoreContainer.style.display = this.hasMore() ? '' : 'none';
    }

    /**
     * A failed click currently looks identical to no click at all. Swapping the label is the
     * cheapest honest signal, and it doubles as the affordance to try again.
     */
    setButtonError(failed) {
      if (!this.loadMoreBtn) return;
      const label = this.loadMoreBtn.querySelector('.load-more-btn__text');
      if (!label) return;
      if (this.defaultBtnLabel === undefined) this.defaultBtnLabel = label.textContent;
      this.loadMoreBtn.classList.toggle('load-more-btn--error', failed);
      label.textContent = failed
        ? this.loadMoreBtn.getAttribute('data-retry-label') || 'Try again'
        : this.defaultBtnLabel;
    }

    /**
     * Sold-out items stay parked while windows remain unfetched. Revealing one early would
     * strand it above in-stock items appended later, which is the seam this whole mechanism
     * exists to remove.
     */
    revealLimit() {
      return this.exhausted ? this.products.length : this.inStock.length;
    }

    loadMore(fromAuto = false) {
      if (this.isFetching) {
        // A prefetch already in flight will not reveal on its own, so record the intent and
        // show the spinner. Without this the button looks dead for the length of the fetch.
        // A user-initiated fetch already reveals when it lands, so it needs nothing here.
        if (this.isPrefetching) {
          this.pendingReveal = true;
          if (this.loadMoreBtn) this.loadMoreBtn.classList.add('is-loading');
        }
        return;
      }

      if (this.visibleCount >= this.revealLimit()) {
        if (this.nextUrl) this.fetchNextPage(true, fromAuto);
        return;
      }

      this.revealNext();
    }

    revealNext() {
      const newVisible = Math.min(this.visibleCount + this.perPage, this.revealLimit());
      for (let i = this.visibleCount; i < newVisible; i++) {
        this.products[i].style.display = '';
      }
      this.visibleCount = newVisible;
      this.updateLoadMoreVisibility();
      this.maybePrefetch();
    }

    /**
     * The seam fetch is ~1.5s and almost entirely server render time, so it cannot be made
     * faster — only moved off the critical path. Once the last known in-stock item is on
     * screen the shopper has a screenful left to read, which is enough cover to fetch the
     * next window unseen. Ten reveals deep is also proof of intent, so little is wasted.
     */
    maybePrefetch() {
      if (this.isFetching || !this.nextUrl || this.prefetchFailed) return;
      if (this.visibleCount < this.revealLimit()) return;
      this.fetchNextPage(false);
    }

    insertItems(items) {
      const incomingInStock = [];
      const incomingSoldOut = [];
      items.forEach((item) => {
        item.style.display = 'none';
        (item.classList.contains('product-item--sold-out')
          ? incomingSoldOut
          : incomingInStock
        ).push(item);
      });

      // Parked sold-out items are the tail, so the first of them is the insertion point that
      // keeps DOM order identical to logical order — which the display slicing relies on.
      const tail = this.soldOut[0] || null;
      incomingInStock.forEach((item) => this.itemsParent.insertBefore(item, tail));
      incomingSoldOut.forEach((item) => this.itemsParent.appendChild(item));

      this.inStock = this.inStock.concat(incomingInStock);
      this.soldOut = this.soldOut.concat(incomingSoldOut);
      this.rebuildOrder();
    }

    /**
     * Shopify caps a `paginate` window at 250 items, so a large collection spans several
     * pages. Fetch the next one via the Section Rendering API and append its items, keeping
     * everything on a single page.
     */
    fetchNextPage(reveal = true, fromAuto = false) {
      if (!this.itemsParent || !this.nextUrl) return;

      const url = new URL(this.nextUrl, window.location.origin);
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);

      this.isFetching = true;
      this.isPrefetching = !reveal;
      let ok = true;
      // A prefetch is invisible by definition, so it must not touch the button.
      if (reveal && this.loadMoreBtn) {
        this.loadMoreBtn.classList.add('is-loading');
        this.setButtonError(false);
      }

      fetch(url.toString())
        .then((response) => {
          if (!response.ok) throw new Error('Pagination fetch failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const grid = doc.querySelector('[data-products-grid]');
          if (!grid) {
            // A response with no grid is malformed rather than a network failure, and there is
            // nothing further to ask for. Treat it as the end so nothing stays parked forever.
            this.nextUrl = '';
            this.exhausted = true;
            return;
          }

          const incoming = Array.from(
            grid.querySelectorAll('.grid-item:not(.product-item--featured-image)')
          );

          const nextContainer = grid.querySelector('[data-load-more-container]');
          this.nextUrl = nextContainer
            ? nextContainer.getAttribute('data-next-url') || ''
            : '';
          // The server has spoken: no next window means the collection really is complete,
          // which is what releases the parked sold-out items.
          this.exhausted = !this.nextUrl;

          // Items only. The fetched section carries its own <collection-pagination> and
          // load-more container; appending those would upgrade a rival instance.
          // nextUrl is read first so revealLimit() already accounts for this window.
          this.insertItems(incoming);

          this.fetchFailures = 0;
          // Re-arm prefetching only once something has actually succeeded. Clearing this when a
          // fetch *starts* meant every failed click also fired a doomed background retry.
          this.prefetchFailed = false;

          // An all-sold-out window adds nothing revealable, so one click would appear to do
          // nothing. Chain to the next window instead, once isFetching has been cleared.
          if (this.visibleCount >= this.revealLimit() && this.nextUrl) {
            this.chainFetch = true;
          } else if (reveal) {
            this.revealNext();
          }
        })
        .catch((error) => {
          // nextUrl survives a failure so the button stays and the click can be retried;
          // clearing it on the first blip would hide paging permanently. Give up after a few
          // so load_more_auto cannot retry on every intersection forever.
          console.warn('[collection-pagination] next page fetch failed', error);
          ok = false;
          if (fromAuto) {
            // Only the observer needs a stop condition: it re-fires on every intersection, so a
            // persistent failure would retry forever. Giving up here also ends the collection,
            // which is why this is reserved for the one caller that cannot stop itself.
            this.fetchFailures = (this.fetchFailures || 0) + 1;
            if (this.fetchFailures >= 3) {
              this.nextUrl = '';
              this.exhausted = true;
            }
          } else if (reveal) {
            // A human clicking a button is not a runaway. Keep nextUrl so the next click
            // retries, and show the failure instead of leaving the button looking inert.
            this.setButtonError(true);
          } else {
            // Without this, .finally's maybePrefetch() would re-fire immediately — the
            // conditions that triggered it are unchanged, and prefetches take no strike.
            this.prefetchFailed = true;
          }
        })
        .finally(() => {
          this.isFetching = false;
          this.isPrefetching = false;
          if (this.loadMoreBtn) this.loadMoreBtn.classList.remove('is-loading');
          this.updateLoadMoreVisibility();

          // The chained fetch inherits the original intent, so a click waiting on a prefetch
          // is still honoured once the chain resolves.
          if (this.chainFetch) {
            this.chainFetch = false;
            this.fetchNextPage(reveal || this.pendingReveal, fromAuto);
            return;
          }

          if (this.pendingReveal) {
            this.pendingReveal = false;
            // A failed prefetch appended nothing, so revealing would do nothing visible.
            // Redo it as the click the shopper actually made.
            if (ok) {
              this.revealNext();
            } else {
              this.fetchNextPage(true, fromAuto);
            }
            return;
          }

          this.maybePrefetch();
        });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onVisualFiltersUpdated) {
        document.removeEventListener('visual-filters:updated', this.onVisualFiltersUpdated);
      }
    }

    // ── Numbered pages ──────────────────────────────────────────────────────
    initPaged() {
      const param = parseInt(new URLSearchParams(window.location.search).get('available_page'), 10);
      this.currentPage = param && param >= 1 && param <= this.totalPages ? param : 1;

      this.showCurrentPage(false);
      this.addPagePreviews();

      // The visual colour/undertone filters hide items with the same `display` we use, and
      // they show every match regardless of page. So on their signal we only refresh the
      // pagination controls — re-applying the page slice would undo the filter.
      this.onVisualFiltersUpdated = () => {
        this.partition(this.gridItems());
        if (this.currentPage > this.totalPages) this.currentPage = 1;
        this.refreshControls();
        this.addPagePreviews();
      };
      document.addEventListener('visual-filters:updated', this.onVisualFiltersUpdated);

      // Scoped to the container so listeners die with the DOM the filter form replaces.
      this.container.addEventListener('click', (e) => {
        // Real ?page=N links cross a Shopify page boundary, which client-side slicing cannot.
        if (e.target.closest('[data-shopify-page]')) return;

        const pageLink = e.target.closest('.pagination-custom__page');
        if (pageLink && !pageLink.classList.contains('pagination-custom__page--active')) {
          const newPage = parseInt(pageLink.textContent.trim(), 10);
          if (newPage && newPage !== this.currentPage) {
            e.preventDefault();
            this.goToPage(newPage);
          }
          return;
        }

        if (e.target.closest('.pagination-custom__prev') && this.currentPage > 1) {
          e.preventDefault();
          this.goToPage(this.currentPage - 1);
          return;
        }

        if (e.target.closest('.pagination-custom__next') && this.currentPage < this.totalPages) {
          e.preventDefault();
          this.goToPage(this.currentPage + 1);
        }
      });
    }

    goToPage(page) {
      this.currentPage = page;
      const url = new URL(window.location);
      if (page === 1) {
        url.searchParams.delete('available_page');
      } else {
        url.searchParams.set('available_page', page);
      }
      window.history.pushState({}, '', url);
      this.showCurrentPage(true);
      this.addPagePreviews();
    }

    pagePreview(pageNumber) {
      const total = this.products.length;
      const start = (pageNumber - 1) * this.perPage + 1;
      const end = Math.min(pageNumber * this.perPage, total);
      return 'Products ' + start + '-' + end + ' of ' + total;
    }

    showCurrentPage(scrollToTop) {
      const startIndex = (this.currentPage - 1) * this.perPage;
      const endIndex = startIndex + this.perPage;

      this.products.forEach((product, index) => {
        product.style.display = index >= startIndex && index < endIndex ? '' : 'none';
      });

      this.refreshControls();

      if (scrollToTop) setTimeout(() => this.scrollToTop(), 150);
    }

    refreshControls() {
      this.container
        .querySelectorAll('.pagination-custom__page:not([data-shopify-page])')
        .forEach((link) => {
          link.classList.toggle(
            'pagination-custom__page--active',
            link.textContent.trim() === String(this.currentPage)
          );
        });

      const prev = this.container.querySelector('.pagination-custom__prev');
      const next = this.container.querySelector('.pagination-custom__next');
      if (prev) prev.classList.toggle('is-hidden', this.currentPage <= 1);
      if (next) next.classList.toggle('is-hidden', this.currentPage >= this.totalPages);
    }

    addPagePreviews() {
      this.container
        .querySelectorAll(
          '.pagination-custom__page:not(.pagination-custom__page--active):not([data-shopify-page])'
        )
        .forEach((link) => {
          const pageNum = parseInt(link.textContent.trim(), 10);
          if (pageNum && pageNum !== this.currentPage) {
            link.setAttribute('title', this.pagePreview(pageNum));
          }
        });

      const prev = this.container.querySelector('.pagination-custom__prev');
      if (prev && this.currentPage > 1) {
        prev.setAttribute('title', 'Previous page: ' + this.pagePreview(this.currentPage - 1));
      }

      const next = this.container.querySelector('.pagination-custom__next');
      if (next && this.currentPage < this.totalPages) {
        next.setAttribute('title', 'Next page: ' + this.pagePreview(this.currentPage + 1));
      }
    }
  }

  customElements.define('collection-pagination', CollectionPagination);
}
